/**
 * shadcn/Tailwind replacement for BinaryAttributeField.
 * Binary schema attribute: size label + upload/clear; never shows raw base64.
 */

import React from 'react';
import { Upload, X } from 'lucide-react';
import { createEntityRef } from '../../types';
import { isARef } from '../../utils/entityPropertyUtils';
import {
    formatTier3LengthCountSizeEstimate,
    getTargetEntitlementItemForAttribute,
    type EntityEntitlementForTargetResponse,
} from '../../types/EntityEntitlements';
import ContentTypesCacheService from '../../services/ContentTypesCacheService';
import { cn } from '../../lib/utils';

export interface ShadcnBinaryAttributeFieldProps {
    name: string;
    label: React.ReactNode;
    value: unknown;
    loading: boolean;
    isViewSurface: boolean;
    readOnly: boolean;
    isEditMode: boolean;
    isFile: boolean;
    schemaAttributes: Array<{ name?: string; id?: string; value?: string }>;
    targetEntitlement: EntityEntitlementForTargetResponse | null;
    onFieldChange: (fieldName: string, value: unknown) => void;
    onError: (message: string) => void;
}

export const ShadcnBinaryAttributeField: React.FC<ShadcnBinaryAttributeFieldProps> = ({
    name,
    label,
    value,
    loading,
    isViewSurface,
    readOnly,
    isEditMode,
    isFile,
    schemaAttributes,
    targetEntitlement,
    onFieldChange,
    onError,
}) => {
    const hasExistingData = Boolean(value && typeof value === 'string' && value.length > 0);
    const approxBytes = hasExistingData ? Math.round((value as string).length * 0.75) : 0;
    const tier3ItemForBinary = getTargetEntitlementItemForAttribute(targetEntitlement, name);
    const tier3Attr = tier3ItemForBinary?.Attribute;
    const isNonIndexedBinary = tier3Attr?.Indexed === false;
    const tier3Length = tier3Attr?.Length;
    const tier3Count = tier3Attr?.Count;
    const hasTier3Estimate =
        (typeof tier3Length === 'number' && tier3Length > 0) || (typeof tier3Count === 'number' && tier3Count > 0);
    const isLikelyNotLoaded = isEditMode && isNonIndexedBinary && !hasExistingData;
    const sizeLabel =
        approxBytes >= 1024 * 1024
            ? `${(approxBytes / 1024 / 1024).toFixed(1)} MB`
            : approxBytes >= 1024
              ? `${(approxBytes / 1024).toFixed(1)} KB`
              : approxBytes > 0
                ? `${approxBytes} B`
                : '';
    const tier3EstimateLabel = formatTier3LengthCountSizeEstimate(tier3Length, tier3Count) ?? '';

    const handleBinaryFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;
        event.target.value = '';
        let contentType = file.type || '';
        if (!contentType && file.name) {
            try {
                const ext = file.name.includes('.') ? file.name.substring(file.name.lastIndexOf('.')) : '';
                const match = await ContentTypesCacheService.getInstance().getContentTypesByExtension(ext);
                if (match?.length) contentType = match[0].ContentType;
            } catch { /* ignore */ }
        }
        try {
            const reader = new FileReader();
            reader.onload = (e) => {
                const result = e.target?.result;
                if (typeof result === 'string') {
                    const base64Data = result.includes(',') ? result.split(',')[1] : result;
                    onFieldChange(name, base64Data);
                    if (isFile && isARef(createEntityRef(name), 'Data')) {
                        const fileNameField = schemaAttributes.find((a) =>
                            isARef(createEntityRef((a.name || a.id || a.value || '').toString().trim()), 'Filename')
                        );
                        if (fileNameField) {
                            const fn = fileNameField.name || fileNameField.id || fileNameField.value;
                            if (fn) onFieldChange(fn, file.name);
                        }
                        if (contentType) {
                            const ctField = schemaAttributes.find((a) =>
                                isARef(createEntityRef((a.name || a.id || a.value || '').toString().trim()), 'ContentType')
                            );
                            if (ctField) {
                                const ct = ctField.name || ctField.id || ctField.value;
                                if (ct) onFieldChange(ct, contentType);
                            }
                        }
                    }
                }
            };
            reader.onerror = () => onError('Failed to read file');
            reader.readAsDataURL(file);
        } catch (err) {
            onError(err instanceof Error ? err.message : 'Failed to process file');
        }
    };

    const displayValue = hasExistingData
        ? `≈ ${sizeLabel} (binary)`
        : isLikelyNotLoaded
          ? `Not loaded*${tier3EstimateLabel ? ` (${tier3EstimateLabel})` : ''}`
          : 'No data';

    const description = hasExistingData
        ? `Approximate size: ${sizeLabel}. Replace by uploading a new file.`
        : isLikelyNotLoaded
          ? `Sensitive, Large, or File value${hasTier3Estimate ? ` (${tier3EstimateLabel})` : ''}. Upload a file to replace it.`
          : !readOnly && !isViewSurface
            ? 'Upload a file to set the value.'
            : undefined;

    const canSet = !readOnly && !isViewSurface;
    const isDisabled = loading || isViewSurface;

    return (
        <div className="mb-4">
            <label className="block text-sm font-medium text-foreground mb-1">
                {label}
            </label>
            <div className="flex items-center gap-2">
                <input
                    type="text"
                    value={displayValue}
                    readOnly
                    disabled={isDisabled}
                    className={cn(
                        'flex-1 h-9 rounded-md border border-input bg-background px-3 py-1 text-sm',
                        'text-muted-foreground cursor-default',
                        'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
                        isDisabled && 'opacity-50',
                    )}
                />
                {canSet && (
                    <div className="flex items-center gap-1.5">
                        <label
                            className={cn(
                                'inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-xs font-medium cursor-pointer transition-colors',
                                'border border-input bg-background hover:bg-muted text-foreground',
                                isDisabled && 'opacity-50 pointer-events-none',
                            )}
                        >
                            <Upload size={14} />
                            Upload
                            <input
                                type="file"
                                className="hidden"
                                onChange={handleBinaryFileChange}
                                disabled={isDisabled}
                            />
                        </label>
                        {hasExistingData && (
                            <button
                                onClick={() => onFieldChange(name, '')}
                                disabled={isDisabled}
                                className={cn(
                                    'inline-flex items-center gap-1 h-8 px-3 rounded-md text-xs font-medium transition-colors',
                                    'border border-destructive/30 text-destructive hover:bg-destructive/10',
                                    isDisabled && 'opacity-50 pointer-events-none',
                                )}
                            >
                                <X size={14} />
                                Clear
                            </button>
                        )}
                    </div>
                )}
            </div>
            {description && (
                <p className="mt-1 text-xs text-muted-foreground">{description}</p>
            )}
        </div>
    );
};
